import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { PricingCard } from '../components/ui/PricingCard';

export default function Pricing() {
  const navigate = useNavigate();
  const { profile } = useAppContext();
  const isPro = profile?.subscription_status === 'active' || profile?.is_lifetime;

  const handleSelect = (url) => {
    window.open(url, '_blank');
  };

  const freeFeatures = [
    'Personal Information',
    'Physical Address',
    'Profile Photo',
    'Social Media Handles',
    'Custom Web Links',
    'Section Reordering'
  ];

  const proFeatures = [
    'Everything in Free',
    'Custom Domain (e.g. yourname.com)',
    'White Labeling (Hide Branding)',
    'Custom Browser Tab Titles',
    'Advanced Analytics (Coming Soon)',
    'Priority Support'
  ];

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <p className="text-lg text-gray-600">
            Choose the plan that fits your professional needs. Upgrade anytime to unlock premium features and custom domains.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Free Plan */}
          <motion.div 
            whileHover={{ y: -5 }}
            className="p-8 rounded-3xl border-2 border-gray-100 bg-gray-50/50 flex flex-col"
          >
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Basic</h3>
              <p className="text-gray-500 text-sm">Perfect for getting started.</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-gray-900">$0</span>
                <span className="text-gray-400 font-medium">/forever</span>
              </div>
            </div>
            <ul className="space-y-4 mb-10 flex-1">
              {freeFeatures.map(f => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-600">
                  <SafeIcon name="Check" className="text-green-500" />
                  {f}
                </li>
              ))}
              {['Custom Domain', 'White Labeling'].map(f => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-300">
                  <SafeIcon name="X" className="text-gray-300" />
                  {f}
                </li>
              ))}
            </ul>
            <Button 
              variant="secondary" 
              className="w-full"
              onClick={() => navigate(profile ? '/dashboard' : '/login')}
            >
              {profile ? 'Current Plan' : 'Get Started'}
            </Button>
          </motion.div>

          {/* Pro Monthly */}
          <PricingCard
            title="Pro Monthly"
            price="5"
            period="month"
            description="All premium features with monthly flexibility."
            features={proFeatures}
            onSelect={() => handleSelect('https://buy.stripe.com/cNi6oI3Ab1YOguw3Ri6Vq04')}
            buttonText={isPro ? 'Manage Plan' : 'Go Pro Monthly'}
          />

          {/* Pro Yearly */}
          <PricingCard
            highlighted
            badge="Best Value"
            title="Pro Yearly"
            price="60"
            period="year"
            description="Get 2 months free + a physical TapBadge."
            features={[
              ...proFeatures,
              'FREE Physical Card or Keychain',
              '2 Months Free'
            ]}
            onSelect={() => handleSelect('https://buy.stripe.com/6oU7sMdaL1YOemoafG6Vq02')}
            buttonText={isPro ? 'Manage Plan' : 'Get Yearly + Free Card'}
          />
        </div>

        <div className="mt-20 p-10 bg-indigo-600 rounded-[3rem] text-center text-white relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-4">Need a team solution?</h2>
            <p className="text-indigo-100 mb-8 max-w-2xl mx-auto text-lg">
              Manage 10+ employees with a centralized dashboard, bulk shipping, and corporate branding.
            </p>
            <Button variant="secondary" className="bg-white text-indigo-600 border-none px-10 py-4 text-lg">
              Contact Sales
            </Button>
          </div>
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-indigo-500 rounded-full blur-3xl opacity-50"></div>
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-purple-500 rounded-full blur-3xl opacity-50"></div>
        </div>
      </div>
    </div>
  );
}
